/**
 * LEOS Mission Ready - Ground Station Strategic Placement
 * Interactive globe-based station placement system
 */

import { apiClient } from './api-integration.js';
import * as BABYLON from '@babylonjs/core';

export class GroundStationPlacement {
    constructor(parentUI) {
        this.parentUI = parentUI;
        this.placementMode = {
            active: false,
            type: 'tracking',
            namePrefix: 'Station',
            mode: 'single',
            stations: []
        };
        this.clickHandler = null;
    }
    
    getPlacementHTML() {
        return `
            <div class="placement-controls">
                <div class="form-row">
                    <div class="form-group">
                        <label>Station Type</label>
                        <select id="placement-station-type">
                            <option value="tracking">Tracking Station</option>
                            <option value="dsn">Deep Space Network</option>
                            <option value="launch">Launch Complex</option>
                            <option value="mission">Mission Control</option>
                            <option value="observatory">Observatory</option>
                            <option value="radar">Radar Station</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Station Name Prefix</label>
                        <input type="text" id="placement-name-prefix" value="Station" placeholder="e.g., LEOS">
                    </div>
                </div>
                
                <div class="placement-mode">
                    <label class="mode-option">
                        <input type="radio" name="placement-mode" value="single" checked>
                        <span>Single Station</span>
                    </label>
                    <label class="mode-option">
                        <input type="radio" name="placement-mode" value="network">
                        <span>Network Mode</span>
                    </label>
                    <label class="mode-option">
                        <input type="radio" name="placement-mode" value="coverage">
                        <span>Coverage Optimization</span>
                    </label>
                </div>
                
                <div class="placement-info">
                    <div class="info-card">
                        <h4>Instructions</h4>
                        <ol>
                            <li>Select station type and naming</li>
                            <li>Choose placement mode</li>
                            <li>Click "Enable Placement Mode"</li>
                            <li>Click on the globe to place stations</li>
                            <li>Review and confirm placement</li>
                        </ol>
                    </div>
                    
                    <div class="placement-status" id="placement-status">
                        <p>Ready to place stations</p>
                    </div>
                </div>
                
                <button class="submit-btn" id="enable-placement">Enable Placement Mode</button>
                
                <div class="placed-stations" id="placed-stations" style="display: none;">
                    <h4>Placed Stations</h4>
                    <div id="placed-stations-list"></div>
                    <button class="submit-btn success" id="confirm-placement">Confirm & Create Stations</button>
                </div>
            </div>
        `;
    }
    
    attachEventHandlers(modal) {
        const enablePlacement = modal.querySelector('#enable-placement');
        const confirmPlacement = modal.querySelector('#confirm-placement');
        
        enablePlacement.addEventListener('click', () => this.enablePlacementMode(modal));
        confirmPlacement.addEventListener('click', () => this.confirmPlacement(modal));
    }
    
    enablePlacementMode(modal) {
        const stationType = modal.querySelector('#placement-station-type').value;
        const namePrefix = modal.querySelector('#placement-name-prefix').value || 'Station';
        const placementMode = modal.querySelector('input[name="placement-mode"]:checked').value;
        
        // Update status
        const status = modal.querySelector('#placement-status');
        status.innerHTML = `
            <p style="color: #10b981;">Placement mode active!</p>
            <p style="color: #94a3b8; font-size: 14px; margin-top: 8px;">
                Click on the globe to place ${placementMode === 'single' ? 'a station' : 'stations'}
            </p>
        `;
        
        // Show placed stations section
        modal.querySelector('#placed-stations').style.display = 'block';
        
        // Initialize placement tracking
        this.placementMode = {
            active: true,
            type: stationType,
            namePrefix: namePrefix,
            mode: placementMode,
            stations: []
        };
        
        // Set up globe click handler
        this.setupGlobeClickHandler();
        
        // Close modal to interact with globe
        this.parentUI.close();
        
        // Show floating placement UI
        this.showPlacementUI();
    }
    
    setupGlobeClickHandler() {
        const scene = window.scene; // Assuming scene is globally accessible
        
        if (!scene) {
            console.error('Scene not available for placement mode');
            return;
        }
        
        // Add placement click handler
        this.clickHandler = (pointerInfo) => {
            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERPICK && this.placementMode.active) {
                const pickResult = pointerInfo.pickInfo;
                
                if (pickResult.hit && pickResult.pickedMesh && pickResult.pickedMesh.name === 'earth') {
                    // Get click position on Earth
                    const hitPoint = pickResult.pickedPoint;
                    
                    // Convert to lat/lon
                    const coords = this.babylonToGeodetic(hitPoint);
                    
                    // Add station at this location
                    this.addPlacementStation(coords.lat, coords.lon);
                    
                    // Add visual marker on the globe
                    this.addVisualMarker(hitPoint, scene);
                }
            }
        };
        
        scene.onPointerObservable.add(this.clickHandler);
    }
    
    babylonToGeodetic(position) {
        // Convert Babylon position to latitude/longitude
        const normalized = position.normalize();
        const lat = Math.asin(normalized.y) * 180 / Math.PI;
        const lon = Math.atan2(normalized.z, normalized.x) * 180 / Math.PI;
        
        return { lat, lon };
    }
    
    addPlacementStation(lat, lon) {
        const stationNumber = this.placementMode.stations.length + 1;
        const station = {
            name: `${this.placementMode.namePrefix} ${stationNumber}`,
            latitude: lat,
            longitude: lon,
            altitude: 0,
            type: this.placementMode.type,
            frequency: 2.3,
            diameter: this.getDefaultDiameter(this.placementMode.type),
            range: this.getDefaultRange(this.placementMode.type),
            description: `Manually placed ${this.placementMode.type} station`
        };
        
        this.placementMode.stations.push(station);
        
        // Update placement UI
        this.updatePlacementUI();
        
        // If single mode, disable further placement
        if (this.placementMode.mode === 'single' && this.placementMode.stations.length === 1) {
            this.placementMode.active = false;
            this.showCompletionMessage();
        }
        
        // If coverage mode, calculate and show coverage
        if (this.placementMode.mode === 'coverage') {
            this.updateCoverageAnalysis();
        }
    }
    
    getDefaultDiameter(type) {
        const diameters = {
            'dsn': 70,
            'launch': 25,
            'tracking': 15,
            'mission': 20,
            'observatory': 20,
            'radar': 40
        };
        return diameters[type] || 15;
    }
    
    getDefaultRange(type) {
        const ranges = {
            'dsn': 15000,
            'launch': 3500,
            'tracking': 3000,
            'mission': 3500,
            'observatory': 4000,
            'radar': 5500
        };
        return ranges[type] || 3000;
    }
    
    addVisualMarker(position, scene) {
        // Create a temporary marker on the globe
        const marker = BABYLON.MeshBuilder.CreateSphere(
            `placement_marker_${this.placementMode.stations.length}`,
            { diameter: 0.01 },
            scene
        );
        marker.position = position.scale(1.002); // Slightly above surface
        
        const mat = new BABYLON.StandardMaterial('placement_marker_mat', scene);
        mat.emissiveColor = new BABYLON.Color3(0, 1, 1); // Cyan
        mat.diffuseColor = new BABYLON.Color3(0, 0.8, 0.8);
        marker.material = mat;
        
        // Parent to Earth if available
        const earthMesh = scene.getMeshByName('earth');
        if (earthMesh) marker.parent = earthMesh;
        
        // Store marker reference
        if (!this.placementMarkers) this.placementMarkers = [];
        this.placementMarkers.push(marker);
    }
    
    showPlacementUI() {
        const placementUI = document.createElement('div');
        placementUI.id = 'floating-placement-ui';
        placementUI.style.cssText = `
            position: fixed;
            top: 100px;
            right: 20px;
            background: rgba(30, 41, 59, 0.95);
            border: 1px solid rgba(148, 163, 184, 0.3);
            border-radius: 12px;
            padding: 20px;
            width: 300px;
            z-index: 1000;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        `;
        
        placementUI.innerHTML = `
            <h3 style="color: #e2e8f0; margin: 0 0 16px 0;">Station Placement Mode</h3>
            <div id="placement-ui-content"></div>
            <button class="submit-btn" style="margin-top: 16px;" onclick="groundStationUploadUI.placementModule.finishPlacement()">
                Finish Placement
            </button>
        `;
        
        document.body.appendChild(placementUI);
        this.updatePlacementUI();
    }
    
    updatePlacementUI() {
        const content = document.querySelector('#placement-ui-content');
        if (!content) return;
        
        content.innerHTML = `
            <p style="color: #94a3b8; margin: 0 0 12px 0;">
                ${this.placementMode.stations.length} station(s) placed
            </p>
            <div style="max-height: 200px; overflow-y: auto;">
                ${this.placementMode.stations.map((station, i) => `
                    <div style="background: rgba(0, 153, 255, 0.1); border: 1px solid rgba(0, 153, 255, 0.3); padding: 8px; border-radius: 6px; margin-bottom: 8px;">
                        <strong style="color: #00d4ff;">${station.name}</strong>
                        <br>
                        <span style="color: #94a3b8; font-size: 12px;">
                            ${station.latitude.toFixed(2)}°, ${station.longitude.toFixed(2)}°
                        </span>
                    </div>
                `).join('')}
            </div>
        `;
    }
    
    updateCoverageAnalysis() {
        // Calculate total coverage area for placed stations
        const totalCoverage = this.calculateTotalCoverage();
        const coveragePercent = (totalCoverage / 510072000) * 100; // Earth's surface area in km²
        
        const content = document.querySelector('#placement-ui-content');
        if (content) {
            const coverageInfo = document.createElement('div');
            coverageInfo.style.cssText = 'margin-top: 12px; padding: 12px; background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.3); border-radius: 6px;';
            coverageInfo.innerHTML = `
                <strong style="color: #10b981;">Coverage Analysis</strong><br>
                <span style="color: #94a3b8; font-size: 14px;">
                    Total Coverage: ${coveragePercent.toFixed(1)}% of Earth's surface
                </span>
            `;
            content.appendChild(coverageInfo);
        }
    }
    
    calculateTotalCoverage() {
        // Simplified coverage calculation based on station range
        let totalArea = 0;
        for (const station of this.placementMode.stations) {
            const radiusKm = station.range || 3000;
            const area = Math.PI * radiusKm * radiusKm;
            totalArea += area;
        }
        return totalArea;
    }
    
    showCompletionMessage() {
        const content = document.querySelector('#placement-ui-content');
        if (content) {
            const message = document.createElement('div');
            message.style.cssText = 'margin-top: 12px; padding: 12px; background: rgba(16, 185, 129, 0.2); border: 1px solid rgba(16, 185, 129, 0.5); border-radius: 6px;';
            message.innerHTML = '<span style="color: #10b981;">Station placed! Click "Finish Placement" to continue.</span>';
            content.appendChild(message);
        }
    }
    
    finishPlacement() {
        // Remove floating UI
        const floatingUI = document.querySelector('#floating-placement-ui');
        if (floatingUI) floatingUI.remove();
        
        // Remove click handler
        if (this.clickHandler && window.scene) {
            window.scene.onPointerObservable.removeCallback(this.clickHandler);
        }
        
        // Disable placement mode
        this.placementMode.active = false;
        
        // Show main modal again with confirmation
        this.parentUI.show();
        
        // Switch to strategic tab and show placed stations
        const modal = document.querySelector('.ground-station-modal');
        this.parentUI.switchTab('strategic', modal);
        
        // Update placed stations list
        const listContainer = document.querySelector('#placed-stations-list');
        listContainer.innerHTML = this.placementMode.stations.map((station, i) => `
            <div class="placed-station-item">
                <span>${station.name} - ${station.latitude.toFixed(2)}°, ${station.longitude.toFixed(2)}°</span>
                <button onclick="groundStationUploadUI.placementModule.removePlacedStation(${i})">Remove</button>
            </div>
        `).join('');
        
        modal.querySelector('#placed-stations').style.display = 'block';
    }
    
    removePlacedStation(index) {
        this.placementMode.stations.splice(index, 1);
        
        // Remove visual marker if it exists
        if (this.placementMarkers && this.placementMarkers[index]) {
            this.placementMarkers[index].dispose();
            this.placementMarkers.splice(index, 1);
        }
        
        // Refresh the display
        this.finishPlacement();
    }
    
    async confirmPlacement(modal) {
        if (!this.placementMode.stations || this.placementMode.stations.length === 0) {
            this.parentUI.showStatus('No stations to create', 'error');
            return;
        }
        
        this.parentUI.showStatus(`Creating ${this.placementMode.stations.length} ground stations...`, 'loading');
        
        try {
            for (const station of this.placementMode.stations) {
                await apiClient.createGroundStation(station);
            }
            
            this.parentUI.showStatus(`Successfully created ${this.placementMode.stations.length} ground stations!`, 'success');
            this.parentUI.loadCurrentStations();
            
            // Clean up markers
            if (this.placementMarkers) {
                this.placementMarkers.forEach(marker => marker.dispose());
                this.placementMarkers = [];
            }
            
            // Clear placement data
            this.placementMode = { active: false, stations: [] };
            
            // Reload scene
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (error) {
            this.parentUI.showStatus(`Error creating stations: ${error.message}`, 'error');
        }
    }
    
    cleanup() {
        // Clean up any markers
        if (this.placementMarkers) {
            this.placementMarkers.forEach(marker => marker.dispose());
            this.placementMarkers = [];
        }
        
        // Remove click handler
        if (this.clickHandler && window.scene) {
            window.scene.onPointerObservable.removeCallback(this.clickHandler);
        }
        
        // Reset placement mode
        this.placementMode = {
            active: false,
            type: 'tracking',
            namePrefix: 'Station',
            mode: 'single',
            stations: []
        };
    }
}